import React, { useState, useEffect } from "react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../../../db/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { AnimatedModal, modalPanelClass } from "../../../components/AnimatedModal";
import { SongArtwork } from "../../../components/SongArtwork";
import { YoutubeVideosFormEditor } from "../../../components/YoutubeVideosFormEditor";
import CifraImportPanel from "../../../components/CifraImportPanel";
import { useTranslation } from "react-i18next";
import {
  buildYoutubeFirestoreUpdate,
  prepareYoutubeLinksForSave,
} from "../../../utils/youtubeLinks";
import {
  HiOutlineXMark,
  HiOutlineMusicalNote,
  HiOutlinePhoto,
  HiOutlineGlobeAlt,
} from "react-icons/hi2";

interface AddSongModalProps {
  onClose: () => void;
  /** Called with the new Firestore song id after a successful save. */
  onSongAdded?: (songId?: string) => void;
  /** Shown inside modal (e.g. when Cifra import could not fetch chords/YouTube) */
  importNotice?: string;
  prefillData?: {
    title?: string;
    artist?: string;
    album?: string;
    youtubeLink?: string;
    youtubeLinks?: string[];
    charts?: string;
    artworkURL?: string;
    cifraclubUrl?: string;
    artistSlug?: string;
    songSlug?: string;
  };
}

const AddSongModal: React.FC<AddSongModalProps> = ({
  onClose,
  onSongAdded,
  importNotice,
  prefillData,
}) => {
  const { t } = useTranslation(); // Initialize translation
  const [title, setTitle] = useState(prefillData?.title || "");
  const [artist, setArtist] = useState(prefillData?.artist || "");
  const [album, setAlbum] = useState(prefillData?.album || "");
  const [youtubeLinks, setYoutubeLinks] = useState<string[]>(
    prefillData?.youtubeLinks?.length
      ? prefillData.youtubeLinks
      : prefillData?.youtubeLink
        ? [prefillData.youtubeLink]
        : [""]
  );
  const [youtubeError, setYoutubeError] = useState("");
  const [charts, setCharts] = useState(prefillData?.charts || "");
  const [artwork, setArtwork] = useState<File | null>(null);
  const [artworkURL, setArtworkURL] = useState(prefillData?.artworkURL || "");
  const [uploadError, setUploadError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [user] = useAuthState(auth);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showImporter, setShowImporter] = useState(false);
  const [localImportNotice, setLocalImportNotice] = useState(importNotice || "");
  const [cifraMeta, setCifraMeta] = useState({
    cifraclubUrl: prefillData?.cifraclubUrl || "",
    artistSlug: prefillData?.artistSlug || "",
    songSlug: prefillData?.songSlug || "",
  });

  useEffect(() => {
    if (prefillData) {
      if (prefillData.title) setTitle(prefillData.title);
      if (prefillData.artist) setArtist(prefillData.artist);
      if (prefillData.album !== undefined) setAlbum(prefillData.album);
      if (prefillData.charts) setCharts(prefillData.charts);
      if (prefillData.youtubeLinks?.length) {
        setYoutubeLinks(prefillData.youtubeLinks);
      } else if (prefillData.youtubeLink) {
        setYoutubeLinks([prefillData.youtubeLink]);
      }
      if (prefillData.artworkURL) setArtworkURL(prefillData.artworkURL);
      setCifraMeta({
        cifraclubUrl: prefillData.cifraclubUrl || "",
        artistSlug: prefillData.artistSlug || "",
        songSlug: prefillData.songSlug || "",
      });
    }
  }, [prefillData]);

  useEffect(() => {
    setLocalImportNotice(importNotice || "");
  }, [importNotice]);

  const handleAddSong = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setSaveError("");
    let finalArtworkURL = artworkURL;

    try {
      if (artwork) {
        const storageRef = ref(storage, `artworks/${artwork.name}`);
        const uploadTask = uploadBytesResumable(storageRef, artwork);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              console.log("Upload is " + progress + "% done");
            },
            (error) => {
              console.error("Error uploading artwork: ", error);
              setUploadError(t("upload_error"));
              reject(error);
            },
            async () => {
              finalArtworkURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve();
            }
          );
        });
      }

      const { links: savedYoutubeLinks, error: youtubeValidationError } =
        prepareYoutubeLinksForSave(youtubeLinks);
      if (youtubeValidationError === "invalid") {
        setYoutubeError(t("invalidYouTubeUrl") || "Please enter a valid YouTube URL.");
        setLoading(false);
        return;
      }
      if (youtubeValidationError === "max") {
        setYoutubeError(
          t("maxYouTubeVideosReached", { max: 10 }) ||
            "You can add up to 10 videos."
        );
        setLoading(false);
        return;
      }

      const docRef = await addDoc(collection(db, "songs"), {
        title,
        artist,
        album,
        ...buildYoutubeFirestoreUpdate(savedYoutubeLinks),
        charts,
        artworkURL: finalArtworkURL,
        ...(cifraMeta.cifraclubUrl ? { cifraclubUrl: cifraMeta.cifraclubUrl } : {}),
        ...(cifraMeta.artistSlug ? { artistSlug: cifraMeta.artistSlug } : {}),
        ...(cifraMeta.songSlug ? { songSlug: cifraMeta.songSlug } : {}),
        timestamp: serverTimestamp(),
        userId: user?.uid,
      });
      setSuccessMessage(t("song_saved_success"));
      onSongAdded?.(docRef.id);
      setTimeout(() => {
        setSuccessMessage("");
        onClose();
      }, 2000);
    } catch (error: any) {
      console.error("Error adding song: ", error);
      setSaveError(error?.message || t("errorSavingSong") || "Failed to save song. Check that you're signed in and have permission.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleArtworkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setArtwork(e.target.files[0]);
    }
  };

  const inputBase =
    "w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-[#5932EA]/30 focus:border-[#5932EA] transition-all";
  const labelClass = "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";
  const sectionHeader = "block text-sm font-medium text-gray-600 dark:text-gray-400 mb-2";

  return (
    <AnimatedModal isOpen onClose={onClose} contentClassName="sm:max-w-3xl">
      <div className={modalPanelClass}>
        <div className="sm:hidden flex justify-center pt-2 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
        </div>
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50/80 to-white dark:from-gray-800 dark:to-gray-800/80 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-[#5932EA]/10 dark:bg-[#5932EA]/20 text-[#5932EA] shadow-sm">
              <HiOutlineMusicalNote className="w-6 h-6" strokeWidth={2} />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 tracking-tight">
              {t("add_new_song")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            aria-label={t("close")}
          >
            <HiOutlineXMark className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleAddSong} className="flex flex-col flex-1 min-h-0">
          <div className="p-6 overflow-y-auto space-y-6 flex-1">
            {localImportNotice && (
              <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 text-sm border border-amber-200 dark:border-amber-800">
                <strong className="block mb-1">{t("songImporter")}</strong>
                {localImportNotice}
              </div>
            )}

            <div className="rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
              <button
                type="button"
                onClick={() => setShowImporter((v) => !v)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-gray-50/80 dark:bg-gray-900/40 text-left"
              >
                <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-100">
                  <HiOutlineGlobeAlt className="w-4 h-4 text-[#5932EA]" />
                  {t("importFromSongImporter") || "Import from Song Importer"}
                </span>
                <span className="text-xs text-[#5932EA] font-medium">
                  {showImporter ? t("hide") || "Hide" : t("show") || "Show"}
                </span>
              </button>
              {showImporter && (
                <div className="p-4 border-t border-gray-100 dark:border-gray-700">
                  <CifraImportPanel
                    compact
                    initialQuery={[title, artist].filter(Boolean).join(" ")}
                    onImportReady={(prefill, notice) => {
                      if (prefill.title) setTitle(prefill.title);
                      if (prefill.artist) setArtist(prefill.artist);
                      if (prefill.album !== undefined) setAlbum(prefill.album || "");
                      if (prefill.charts) setCharts(prefill.charts);
                      if (prefill.youtubeLinks?.length) {
                        setYoutubeLinks(prefill.youtubeLinks);
                      } else if (prefill.youtubeLink) {
                        setYoutubeLinks([prefill.youtubeLink]);
                      }
                      if (prefill.artworkURL) setArtworkURL(prefill.artworkURL);
                      setCifraMeta({
                        cifraclubUrl: prefill.cifraclubUrl || "",
                        artistSlug: prefill.artistSlug || "",
                        songSlug: prefill.songSlug || "",
                      });
                      setLocalImportNotice(notice || "");
                      setShowImporter(false);
                    }}
                  />
                </div>
              )}
            </div>

            <div>
              <label htmlFor="title" className={sectionHeader}>{t("title")}</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                className={inputBase}
                maxLength={100}
                placeholder={t("title")}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="artist" className={sectionHeader}>{t("artist")}</label>
                <input
                  type="text"
                  id="artist"
                  value={artist}
                  onChange={(e) => setArtist(e.target.value)}
                  required
                  className={inputBase}
                  maxLength={100}
                  placeholder={t("artist")}
                />
              </div>
              <div>
                <label htmlFor="album" className={sectionHeader}>{t("album")}</label>
                <input
                  type="text"
                  id="album"
                  value={album}
                  onChange={(e) => setAlbum(e.target.value)}
                  className={inputBase}
                  maxLength={100}
                  placeholder={t("album")}
                />
              </div>
            </div>
            <div>
              <label htmlFor="charts" className={labelClass}>{t("chords")}</label>
              <textarea
                id="charts"
                value={charts}
                onChange={(e) => setCharts(e.target.value)}
                className={`${inputBase} min-h-[120px] resize-y font-mono text-sm`}
                placeholder="Am  G  C  F..."
              />
            </div>
            <div>
              <label className={sectionHeader}>{t("youtubeVideos") || "YouTube Videos"}</label>
              <YoutubeVideosFormEditor
                links={youtubeLinks}
                onChange={(links) => {
                  setYoutubeLinks(links);
                  setYoutubeError("");
                }}
                error={youtubeError}
                fieldIdPrefix="add-song-youtube"
              />
            </div>
            <div>
              <label className={sectionHeader}>{t("artwork")}</label>
              <div className="flex items-center gap-4">
                <label className="flex-1 cursor-pointer min-w-0">
                  <div className="flex items-center gap-3 px-5 py-4 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30 hover:border-[#5932EA]/50 hover:bg-[#5932EA]/5 transition-all group">
                    <HiOutlinePhoto className="w-6 h-6 text-gray-400 dark:text-gray-500 group-hover:text-[#5932EA] shrink-0 transition-colors" strokeWidth={2} />
                    <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-[#5932EA] truncate transition-colors">
                      {artwork ? artwork.name : t("chooseFileButton")}
                    </span>
                  </div>
                  <input
                    type="file"
                    id="artwork"
                    accept="image/*"
                    onChange={handleArtworkChange}
                    className="hidden"
                  />
                </label>
                {(artworkURL || artwork) && (
                  <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 shrink-0 border border-gray-200 dark:border-gray-600 shadow-sm">
                    {artwork ? (
                      <img src={URL.createObjectURL(artwork)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <SongArtwork src={artworkURL} alt="" className="w-full h-full" iconClassName="w-8 h-8" />
                    )}
                  </div>
                )}
              </div>
              {uploadError && (
                <p className="text-red-500 dark:text-red-400 text-sm mt-1.5">{uploadError}</p>
              )}
            </div>
            {successMessage && (
              <p className="text-emerald-600 dark:text-emerald-400 text-sm text-center py-2">
                {successMessage}
              </p>
            )}
            {saveError && (
              <p className="text-red-500 dark:text-red-400 text-sm py-2">
                {saveError}
              </p>
            )}
          </div>
          <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 shrink-0">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 rounded-xl font-medium bg-[#5932EA] text-white hover:bg-[#4a28c7] transition-all disabled:opacity-70 flex items-center justify-center gap-2 shadow-sm hover:shadow"
            >
              {loading ? (
                <>
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("saving")}
                </>
              ) : (
                t("save")
              )}
            </button>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
};

export default AddSongModal;
